import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  FileExcelOutlined,
  MoreOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Input,
  MenuProps,
  Select,
  Space,
  Tag,
  Tooltip,
  DatePicker,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import { BankResponse } from '../../model/Bank.model';

interface MaturityTableColumnsProps {
  employeeNames: { [key: number]: string };
  maturitySchedules: MaturityScheduleResponse[];
  formatCurrency: (amount: number) => string;
  formatCardNumber: (cardNumber: string) => string;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getTotalDepositAmount: (record: MaturityScheduleResponse) => number;
  getTotalWithdrawalAmount: (record: MaturityScheduleResponse) => number;
  getRemainingAmount: (record: MaturityScheduleResponse) => number;
  getBalanceDifference: (record: MaturityScheduleResponse) => number;
  getTotalAmount: (record: MaturityScheduleResponse) => number;
  getPaymentAmount: (maturityScheduleId: number) => number;
  handleDeposit: (record: MaturityScheduleResponse) => void;
  handleWithdrawal: (record: MaturityScheduleResponse) => void;
  handleExpand: (expanded: boolean, record: MaturityScheduleResponse) => void;
  isRowExpanded: (recordId: number) => boolean;
  handleEdit: (record: MaturityScheduleResponse) => void;
  handleDelete: (record: MaturityScheduleResponse) => void;
  handleToggleCompletion: (record: MaturityScheduleResponse) => void;
  handleExportExcel: (record: MaturityScheduleResponse) => void;
  handleRecordPayment: (record: MaturityScheduleResponse) => void;
  editingNotes: { [key: number]: boolean };
  editingStatus: { [key: number]: boolean };
  tempNotes: { [key: number]: string };
  tempStatus: { [key: number]: string };
  inlineLoading: { [key: number]: boolean };
  handleEditNotes: (record: MaturityScheduleResponse) => void;
  handleSaveNotes: (record: MaturityScheduleResponse) => void;
  handleCancelEditNotes: (record: MaturityScheduleResponse) => void;
  handleEditStatus: (record: MaturityScheduleResponse) => void;
  handleSaveStatus: (
    record: MaturityScheduleResponse,
    newStatus?: string
  ) => void;
  handleCancelEditStatus: (record: MaturityScheduleResponse) => void;
  setTempNotes: (notes: { [key: number]: string }) => void;
  setTempStatus: (status: { [key: number]: string }) => void;
  getStatusOptions: (
    record: MaturityScheduleResponse
  ) => Array<{ value: string; label: string }>;
  isDepositDisabled: (record: MaturityScheduleResponse) => boolean;
  isWithdrawalDisabled: (record: MaturityScheduleResponse) => boolean;
  isAdmin?: boolean; // Chỉ admin mới thấy nút "Đánh dấu hoàn thành"
  onSearch?: (keyword: string) => void;
  banks?: BankResponse[];
}

// Get nested value from object using dot notation
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export const createMaturityTableColumns = ({
  employeeNames,
  maturitySchedules,
  formatCurrency,
  formatCardNumber,
  getStatusColor,
  getStatusText,
  getTotalDepositAmount,
  getTotalWithdrawalAmount,
  getRemainingAmount,
  getBalanceDifference,
  getTotalAmount,
  getPaymentAmount,
  handleDeposit,
  handleWithdrawal,
  handleExpand,
  isRowExpanded,
  handleEdit,
  handleDelete,
  handleToggleCompletion,
  handleExportExcel,
  handleRecordPayment,
  editingNotes,
  editingStatus,
  tempNotes,
  tempStatus,
  inlineLoading,
  handleEditNotes,
  handleSaveNotes,
  handleCancelEditNotes,
  handleEditStatus,
  handleSaveStatus,
  handleCancelEditStatus,
  setTempNotes,
  setTempStatus,
  getStatusOptions,
  isDepositDisabled,
  isWithdrawalDisabled,
  isAdmin = false,
  onSearch,
  banks = [],
}: MaturityTableColumnsProps): ColumnsType<MaturityScheduleResponse> => {
  // Extract unique card types from data
  const uniqueCardTypes = Array.from(
    new Set(
      maturitySchedules
        .map((ms) => ms.creditCard?.cardType?.name)
        .filter(Boolean)
    )
  ).map((cardType) => ({ text: cardType, value: cardType }));

  // Extract unique banks from provided bank list OR data
  const filterBanks = banks.length > 0
    ? banks
    : Array.from(
        new Map(
          maturitySchedules
            .map((ms) => ms.creditCard?.bank)
            .filter((bank): bank is any => !!bank && !!bank.id)
            .map((bank) => [bank.id, bank])
        ).values()
      );

  const uniqueBanks = filterBanks.map((bank: any) => ({
    text: bank.name,
    value: bank.id,
  }));

  // Get column search props
  const getColumnSearchProps = (
    dataIndex: string,
    placeholder: string = 'Tìm kiếm...'
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            confirm();
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => {
              clearFilters && clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: () => true, // Disable client-side filtering
  });

  return [
    // {
    //   title: 'ID',
    //   dataIndex: 'id',
    //   key: 'id',
    //   width: 50,
    //   render: (id: number) => (
    //     <span className="font-mono text-sm text-gray-600">{id}</span>
    //   ),
    //   ...getColumnSearchProps('id'),
    // },
    {
      title: 'Thẻ tín dụng',
      key: 'creditCard',
      width: 90,
      render: (_, record) => (
        <div className="space-y-1">
          <div className="font-medium text-gray-800">
            {formatCardNumber(record.creditCard?.cardNumber)}
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Chủ thẻ: </span>
            <span className="font-medium text-blue-600">
              {record.creditCard?.cardHolderName || 'N/A'}
            </span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Khách hàng: </span>
            <span className="font-medium text-green-600">
              {record.creditCard?.customer?.name || 'N/A'}
            </span>
          </div>
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm theo 4 số cuối hoặc tên chủ thẻ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                if (onSearch) {
                  onSearch(selectedKeys[0]);
                }
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => {
                clearFilters && clearFilters();
                confirm();
                if (onSearch) {
                  onSearch('');
                }
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: () => true,
    },
    // {
    //   title: 'Loại thẻ',
    //   key: 'cardType',
    //   width: 100,
    //   render: (_, record) => (
    //     <div className="text-center">
    //       <Tag color="default">
    //         {record.creditCard?.cardType?.name || 'N/A'}
    //       </Tag>
    //     </div>
    //   ),
    //   filters: uniqueCardTypes,
    //   onFilter: () => true,
    // },
    {
      title: 'Ngân hàng',
      key: 'bank',
      width: 60,
      render: (_, record) => (
        <div className="space-y-1">
          <div className="font-medium text-gray-800">
            {record.creditCard?.bank?.name || 'N/A'}
          </div>
        </div>
      ),
      filters: uniqueBanks,
      filterSearch: true,
      onFilter: () => true,
    },
    {
      title: 'Ngày thanh toán',
      key: 'cyclePaymentDate',
      width: 60,
      render: (_, record) => {
        const cyclePaymentDate = record.creditCard?.cyclePaymentDate;
        return cyclePaymentDate
          ? `Ngày ${cyclePaymentDate} hàng tháng`
          : 'Chưa thiết lập';
      },
      filters: [
        { text: 'Chưa thiết lập', value: 0 },
        ...Array.from({ length: 31 }, (_, i) => ({
          text: `Ngày ${i + 1}`,
          value: i + 1,
        })),
      ],
      filterSearch: true,
      filterMultiple: true,
      onFilter: () => true,
    },
    {
      title: 'Hạn đáo',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 80,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys[0] ? selectedKeys[0] : null}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates] : []);
            }}
            style={{ marginBottom: 8, display: 'flex' }}
            format="DD-MM-YYYY"
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => {
                clearFilters && clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: () => true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string, record: MaturityScheduleResponse) => {
        if (editingStatus[record.id]) {
          return (
            <div className="flex items-center space-x-1">
              <Select
                value={tempStatus[record.id] || status}
                onChange={(value) => {
                  setTempStatus({ ...tempStatus, [record.id]: value });
                  handleSaveStatus(record, value); // Gọi luôn khi chọn
                }}
                options={getStatusOptions(record)}
                size="small"
                style={{ width: 120 }}
                loading={inlineLoading[record.id]}
              />
            </div>
          );
        }

        return (
          <div className="flex items-center justify-between">
            <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditStatus(record)}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        );
      },
      filters: [
        { text: 'Chưa làm', value: 'PENDING' },
        { text: 'Quá hạn', value: 'OVERDUE' },
        { text: 'Đã nạp đáo', value: 'PAID' },
        { text: 'Đã hoàn thành', value: 'COMPLETED' },
        { text: 'Không làm', value: 'ABANDONED' },
      ],
      onFilter: () => true,
    },
    {
      title: 'Số tiền đáo hạn',
      dataIndex: 'repaymentAmount',
      key: 'repaymentAmount',
      width: 90,
      render: (amount: number) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
      ...getColumnSearchProps('repaymentAmount'),
    },
    // {
    //   title: 'Phí thu (%)',
    //   dataIndex: 'feeApplied',
    //   key: 'feeApplied',
    //   width: 40,
    //   render: (fee: number) => {
    //     if (!fee && fee !== 0) return '-';
    //     return `${fee.toFixed(2)}`;
    //   },
    //   ...getColumnSearchProps('feeApplied'),
    // },
    {
      title: 'Còn phải nạp',
      key: 'remainingAmount',
      width: 90,
      render: (_, record) => {
        const amount = getRemainingAmount(record);
        return (
          <span
            className={`font-semibold ${amount > 0 ? 'text-orange-600' : 'text-gray-500'}`}
          >
            {formatCurrency(amount)}
          </span>
        );
      },
      ...getColumnSearchProps('remainingAmount'),
    },
    {
      title: 'Đã nạp',
      key: 'totalDeposit',
      width: 80,
      render: (_, record) => {
        const amount = getTotalDepositAmount(record);
        return <span className="font-semibold">{formatCurrency(amount)}</span>;
      },
      ...getColumnSearchProps('totalDeposit'),
    },
    {
      title: 'Đã rút',
      key: 'totalWithdrawal',
      width: 80,
      render: (_, record) => {
        const amount = getTotalWithdrawalAmount(record);
        return <span className="font-semibold">{formatCurrency(amount)}</span>;
      },
      ...getColumnSearchProps('totalWithdrawal'),
    },
    {
      title: 'Chênh lệch',
      key: 'balanceDifference',
      width: 80,
      render: (_, record) => {
        const amount = Math.abs(getBalanceDifference(record));
        return <span className="font-semibold">{formatCurrency(amount)}</span>;
      },
      ...getColumnSearchProps('balanceDifference'),
    },
    {
      title: 'Thành tiền',
      key: 'totalAmount',
      width: 90,
      render: (_, record) => {
        const amount = getTotalAmount(record);
        return (
          <span className="font-semibold text-blue-600">
            {formatCurrency(amount)}
          </span>
        );
      },
      ...getColumnSearchProps('totalAmount'),
    },
    {
      title: 'Thanh toán',
      key: 'paymentAmount',
      width: 90,
      render: (_, record) => {
        const amount = getPaymentAmount(record.id);
        return (
          <span className="font-semibold text-green-600">
            {formatCurrency(amount)}
          </span>
        );
      },
      ...getColumnSearchProps('paymentAmount'),
    },

    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 120,
      render: (notes: string, record: MaturityScheduleResponse) => {
        if (editingNotes[record.id]) {
          return (
            <div className="flex items-center space-x-1">
              <Input.TextArea
                value={
                  tempNotes[record.id] !== undefined
                    ? tempNotes[record.id]
                    : notes || ''
                }
                onChange={(e) =>
                  setTempNotes({ ...tempNotes, [record.id]: e.target.value })
                }
                size="small"
                rows={2}
                style={{ width: 180 }}
                disabled={inlineLoading[record.id]}
              />
              <div className="flex flex-col space-y-1">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => handleSaveNotes(record)}
                  loading={inlineLoading[record.id]}
                  className="text-green-600 hover:text-green-700"
                />
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => handleCancelEditNotes(record)}
                  disabled={inlineLoading[record.id]}
                  className="text-red-600 hover:text-red-700"
                />
              </div>
            </div>
          );
        }

        return (
          <div className="flex items-start justify-between group">
            <div className="text-sm text-red-600 font-bold flex-1 pr-2">
              {notes ? (
                <div
                  style={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.4',
                    fontWeight: 'bold',
                    color: '#dc2626', // Tailwind red-600
                  }}
                >
                  {notes}
                </div>
              ) : (
                <span className="text-gray-400 font-normal">
                  Chưa có ghi chú
                </span>
              )}
            </div>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditNotes(record)}
              className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            />
          </div>
        );
      },
    },
    {
      title: 'Nhân viên phụ trách',
      dataIndex: 'employees',
      key: 'employees',
      width: 100,
      render: (employees: any[]) => (
        <div className="space-y-1">
          {employees?.map((employee, index) => (
            <Tag key={index} className="text-xs">
              {employeeNames[employee.id] || employee.name || 'Loading...'}
            </Tag>
          ))}
        </div>
      ),
      ...getColumnSearchProps('employees', 'Tìm theo tên nhân viên phụ trách'),
      ...getColumnSearchProps('employees', 'Tìm theo tên nhân viên phụ trách'),
      onFilter: () => true,
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 90,
      render: (createdBy: number) => {
        return employeeNames[createdBy] || 'Loading...';
      },
      ...getColumnSearchProps('createdBy', 'Tìm theo tên người tạo'),
      ...getColumnSearchProps('createdBy', 'Tìm theo tên người tạo'),
      onFilter: () => true,
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Chỉnh sửa kỳ đáo hạn',
            icon: <EditOutlined />,
            onClick: () => handleEdit(record),
          },
          {
            key: 'payment',
            label: 'Ghi nhận thanh toán',
            icon: <DollarOutlined />,
            onClick: () => handleRecordPayment(record),
            disabled: record.status === 'ABANDONED',
          },
          {
            key: 'export',
            label: 'Xuất hóa đơn Excel',
            icon: <FileExcelOutlined />,
            onClick: () => handleExportExcel(record),
            disabled: record.status === 'ABANDONED',
          },
          {
            type: 'divider',
          },
          {
            key: 'delete',
            label: 'Xóa kỳ đáo hạn',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDelete(record),
          },
        ];

        return (
          <Space size="small">
            <Tooltip
              title={
                isRowExpanded(record.id) ? 'Ẩn giao dịch' : 'Xem giao dịch'
              }
            >
              <Button
                type="text"
                size="small"
                icon={<UnorderedListOutlined />}
                onClick={() => handleExpand(!isRowExpanded(record.id), record)}
                style={{
                  color: isRowExpanded(record.id) ? '#1890ff' : '#8c8c8c',
                }}
              />
            </Tooltip>

            <Tooltip
              title={
                isDepositDisabled(record)
                  ? 'Không thể nạp tiền cho kỳ đáo hạn "Không làm"'
                  : 'Tạo giao dịch nạp tiền'
              }
            >
              <Button
                type="default"
                size="small"
                icon={<ArrowUpOutlined />}
                onClick={() => handleDeposit(record)}
                disabled={isDepositDisabled(record)}
              />
            </Tooltip>
            <Tooltip
              title={
                isWithdrawalDisabled(record)
                  ? 'Không thể rút tiền cho kỳ đáo hạn "Không làm"'
                  : 'Tạo giao dịch rút tiền'
              }
            >
              <Button
                type="default"
                size="small"
                icon={<ArrowDownOutlined />}
                onClick={() => handleWithdrawal(record)}
                disabled={isWithdrawalDisabled(record)}
              />
            </Tooltip>
            {isAdmin && (
              <Tooltip
                title={
                  record.status === 'ABANDONED'
                    ? 'Không thể đánh dấu hoàn thành cho kỳ đáo hạn "Không làm"'
                    : record.status === 'COMPLETED'
                      ? 'Bỏ đánh dấu hoàn thành'
                      : 'Đánh dấu hoàn thành'
                }
              >
                <Button
                  type="default"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleCompletion(record)}
                  disabled={record.status === 'ABANDONED'}
                  style={{
                    color: record.status === 'COMPLETED' ? '#52c41a' : undefined,
                  }}
                />
              </Tooltip>
            )}
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button type="default" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];
};
